import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import UploadDialog from "@/components/UploadDialog";
import PetDialog from "@/components/PetDialog";
import PhotoCard from "@/components/PhotoCard";
import SelectionBar from "@/components/SelectionBar";
import { api, photoUrl } from "@/lib/api";
import { PawPrint, Pencil, Trash2, Cake, Camera, Plus, Share2, Copy, Check, Globe, GlobeLock, CheckSquare, X } from "lucide-react";
import { toast } from "sonner";
import { PET, SHARE } from "@/constants/testIds";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

export default function PetDetail() {
  const { petId } = useParams();
  const navigate = useNavigate();
  const [pet, setPet] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const petArr = useMemo(() => (pet ? [pet] : []), [pet]);

  const load = async () => {
    try {
      const [petRes, phRes] = await Promise.all([
        api.get(`/pets/${petId}`),
        api.get("/photos", { params: { pet_id: petId } }),
      ]);
      setPet(petRes.data);
      setPhotos(phRes.data);
    } catch {
      navigate("/");
    }
  };

  useEffect(() => { load(); }, [petId]);

  const del = async () => {
    if (!confirm(`Delete ${pet.name}? Their photos stay but become unassigned.`)) return;
    await api.delete(`/pets/${petId}`);
    toast.success("Pet removed");
    navigate("/");
  };

  const toggleShare = async (enabled) => {
    try {
      if (enabled) {
        const { data } = await api.post(`/pets/${petId}/share`);
        setPet((prev) => ({ ...prev, share_token: data.share_token, share_enabled: true }));
        toast.success("Shareable link ready");
      } else {
        await api.delete(`/pets/${petId}/share`);
        setPet((prev) => ({ ...prev, share_enabled: false }));
        toast.success("Sharing turned off");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const shareUrl = pet?.share_token ? `${window.location.origin}/share/${pet.share_token}` : "";
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied!");
    } catch { toast.error("Couldn't copy"); }
  };

  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const clearSel = () => { setSelected([]); setSelectionMode(false); };

  if (!pet) return null;

  return (
    <div className="min-h-screen">
      <Nav onUpload={() => setUploadOpen(true)} />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 relative z-10">
        <div className="rounded-3xl bg-white border border-[#EAE2D6] warm-shadow overflow-hidden mb-10">
          <div className="grid md:grid-cols-3">
            <div className="aspect-square md:aspect-auto relative" style={{ backgroundColor: `${pet.color}22` }}>
              {pet.cover_photo_id ? (
                <img src={photoUrl(pet.cover_photo_id)} alt={pet.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <PawPrint className="w-24 h-24" style={{ color: pet.color }} />
                </div>
              )}
              <div className="absolute top-4 left-4 tape-strip" />
            </div>
            <div className="md:col-span-2 p-8">
              <div className="font-hand text-2xl mb-1" style={{ color: pet.color }}>meet</div>
              <h1 className="font-display text-6xl font-bold text-[#2D232E] tracking-tight leading-none mb-3">{pet.name}</h1>
              <div className="flex flex-wrap gap-2 mb-5">
                <span className="text-sm font-semibold px-3 py-1 rounded-full capitalize" style={{ backgroundColor: `${pet.color}22`, color: pet.color }}>
                  {pet.species}
                </span>
                {pet.breed && (
                  <span className="text-sm font-semibold px-3 py-1 rounded-full bg-[#F4EFE6] text-[#6A606C]">
                    {pet.breed}
                  </span>
                )}
                {pet.birthday && (
                  <span className="text-sm font-semibold px-3 py-1 rounded-full bg-[#F4EFE6] text-[#6A606C] inline-flex items-center gap-1">
                    <Cake className="w-3.5 h-3.5" /> {new Date(pet.birthday).toLocaleDateString()}
                  </span>
                )}
                <span className="text-sm font-semibold px-3 py-1 rounded-full bg-[#F4EFE6] text-[#6A606C] inline-flex items-center gap-1">
                  <Camera className="w-3.5 h-3.5" /> {photos.length} photos
                </span>
              </div>
              {pet.notes && (
                <p className="font-hand text-2xl text-[#6A606C] mb-6 max-w-xl">{pet.notes}</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setUploadOpen(true)}
                  className="rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white font-semibold px-5 py-2.5 shadow-[0_6px_20px_rgba(231,111,81,0.25)] inline-flex items-center gap-1.5 transition-transform hover:scale-105"
                >
                  <Plus className="w-4 h-4" /> Add photo
                </button>
                <button
                  data-testid={SHARE.toggleBtn}
                  onClick={() => setShareOpen(true)}
                  className="rounded-full bg-white border border-[#EAE2D6] hover:border-[#2A9D8F] text-[#2D232E] font-semibold px-5 py-2.5 inline-flex items-center gap-1.5"
                >
                  <Share2 className="w-4 h-4" /> Share
                  {pet.share_enabled && <span className="w-2 h-2 rounded-full bg-[#2A9D8F]" />}
                </button>
                <button
                  onClick={() => setEditOpen(true)}
                  className="rounded-full bg-white border border-[#EAE2D6] hover:border-[#E76F51] text-[#2D232E] font-semibold px-5 py-2.5 inline-flex items-center gap-1.5"
                >
                  <Pencil className="w-4 h-4" /> Edit
                </button>
                <button
                  data-testid={PET.deleteBtn}
                  onClick={del}
                  className="rounded-full bg-white border border-[#EAE2D6] hover:border-red-300 text-[#6A606C] hover:text-red-600 font-semibold px-5 py-2.5 inline-flex items-center gap-1.5"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        {photos.length === 0 ? (
          <div className="rounded-3xl bg-white border border-[#EAE2D6] p-14 text-center warm-shadow">
            <div className="w-16 h-16 rounded-full bg-[#F4EFE6] flex items-center justify-center mx-auto mb-4">
              <Camera className="w-7 h-7 text-[#E76F51]" />
            </div>
            <h3 className="font-display text-2xl font-bold text-[#2D232E]">No photos of {pet.name} yet</h3>
            <p className="text-[#6A606C] mt-1">Add their first snapshot to start the scrapbook.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-3xl font-bold text-[#2D232E]">Photos</h2>
              <button
                onClick={() => { if (selectionMode) clearSel(); else setSelectionMode(true); }}
                className={`rounded-full font-semibold px-4 py-2 inline-flex items-center gap-1.5 text-sm ${
                  selectionMode ? "bg-[#2D232E] text-white" : "bg-white border border-[#EAE2D6] text-[#2D232E] hover:border-[#E76F51]"
                }`}
              >
                {selectionMode ? <><X className="w-4 h-4" /> Cancel</> : <><CheckSquare className="w-4 h-4" /> Select</>}
              </button>
            </div>
            <div className="columns-2 md:columns-3 lg:columns-4 gap-6">
              {photos.map((p, i) => (
                <PhotoCard
                  key={p.id}
                  photo={p}
                  index={i}
                  pets={petArr}
                  onUpdate={load}
                  onDelete={load}
                  selectionMode={selectionMode}
                  selected={selected.includes(p.id)}
                  onToggleSelect={toggleSelect}
                  onCoverChanged={load}
                />
              ))}
            </div>
          </>
        )}
      </main>
      <SelectionBar selectedIds={selected} onClear={clearSel} onChanged={load} />
      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} defaultPetId={petId} onUploaded={load} />
      <PetDialog open={editOpen} onOpenChange={setEditOpen} initialPet={pet} onSaved={load} />

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl border-[#EAE2D6] bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#2D232E] flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#2A9D8F]" /> Share {pet.name}&apos;s scrapbook
            </DialogTitle>
            <DialogDescription className="font-hand text-lg text-[#E76F51]">
              anyone with the link can view, but not edit
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between rounded-2xl bg-white border border-[#EAE2D6] p-4 mt-2">
            <div className="flex items-center gap-2">
              {pet.share_enabled ? <Globe className="w-5 h-5 text-[#2A9D8F]" /> : <GlobeLock className="w-5 h-5 text-[#6A606C]" />}
              <div>
                <div className="font-semibold text-[#2D232E]">{pet.share_enabled ? "Public link on" : "Private"}</div>
                <div className="text-xs text-[#6A606C]">
                  {pet.share_enabled ? "Turn off to revoke access" : "Turn on to create a link"}
                </div>
              </div>
            </div>
            <Switch checked={pet.share_enabled || false} onCheckedChange={toggleShare} />
          </div>

          {pet.share_enabled && pet.share_token && (
            <div className="rounded-2xl bg-white border border-[#EAE2D6] p-3 flex items-center gap-2">
              <input
                data-testid={SHARE.linkInput}
                readOnly
                value={shareUrl}
                className="flex-1 bg-transparent text-sm text-[#2D232E] focus:outline-none truncate"
                onFocus={(e) => e.target.select()}
              />
              <button
                data-testid={SHARE.copyBtn}
                onClick={copyLink}
                className="rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white font-semibold text-sm px-3 py-1.5 inline-flex items-center gap-1"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
